import React, { useRef, useState } from "react";
import {
  Box, Grid, Typography, Button, Paper, Avatar, Chip,
  Dialog, DialogActions, DialogContent, DialogTitle, TextField, alpha,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import TvIcon from "@mui/icons-material/Tv";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import HistoryIcon from "@mui/icons-material/History";
import StorageIcon from "@mui/icons-material/Storage";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import BusinessIcon from "@mui/icons-material/Business";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import ImageIcon from "@mui/icons-material/Image";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  NAVY, NAVY_DARK, GOLD, GOLD_SOFT, PAGE_BG, BORDER,
  TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../theme/tokens";
import {
  useGetDashboardSummaryQuery,
  useCreatePlaylistMutation,
  useGetSystemHealthQuery,
} from "../../Api/attoDeskApi";
import { useAppSelector } from "../../Store/hooks";
import {
  selectIsSuperAdmin, selectIsContentManager,
} from "../../Store/Auth/AuthSelector";
import PageLoader from "../../Components/Loader/PageLoader";
import { useNotifier } from "../../Core/Notifier";
import { useUploads } from "../../Components/Uploads/UploadContext";

/* ──────────────────────────────────────────────────────────────────────── */

interface IDashboardDevice {
  id: number;
  device_code: string;
  device_name: string;
  company_name: string | null;
  status: "online" | "offline" | "disabled" | "updating" | string;
  last_seen_at: string | null;
  current_media: string | null;
  update_status: string | null;
  screenshot_url: string | null;
}

interface IDashboardCompany {
  id: number;
  name: string;
  subscription_status: string;
  subscription_plan: string | null;
  trial_days_remaining: number | null;
  max_devices: number | null;
  device_count: number;
  online_count: number;
  monthly_revenue: number;
}

interface DashboardData {
  devices: {
    total: number; online: number; offline: number;
    disabled: number; warning: number; active_billable: number;
    alerts_24h: number; uptime_percent: number;
  };
  playlists: { total: number };
  playlist_groups?: { total: number; active: number };
  media: {
    total: number; images: number; videos: number;
    recent_24h: number; storage_bytes: number;
  };
  recent_devices: IDashboardDevice[];
  companies: IDashboardCompany[] | null;
  saas_revenue: {
    mrr: number;
    billable_devices: number;
    avg_per_device: number;
  } | null;
  recent_activity: Array<{
    id: number;
    type: string;
    label: string;
    description: string;
    created_at: string | null;
  }>;
}

const ACTIVITY_STYLE: Record<string, { tint: string; bg: string; icon: React.ReactElement }> = {
  device_registered:        { tint: "#3b82f6", bg: "#dbeafe", icon: <TvIcon /> },
  device_created:           { tint: "#3b82f6", bg: "#dbeafe", icon: <TvIcon /> },
  device_online:            { tint: "#10b981", bg: "#d1fae5", icon: <FiberManualRecordIcon /> },
  device_offline:           { tint: "#ef4444", bg: "#fee2e2", icon: <WifiOffIcon /> },
  device_playlist_assigned: { tint: "#7c3aed", bg: "#ede9fe", icon: <PlaylistPlayIcon /> },
  playlist_updated:         { tint: "#f59e0b", bg: "#fef3c7", icon: <PlaylistPlayIcon /> },
  media_uploaded:           { tint: "#00838f", bg: "#cffafe", icon: <PermMediaIcon /> },
};

/**
 * Activity types we deliberately hide from the dashboard feed.
 * They're still recorded in /signage-activity for audit — the
 * "View All Activity" button gets you there. Tight blocklist so
 * new event types automatically appear unless explicitly noisy.
 */
const NOISY_ACTIVITY_TYPES = new Set([
  "device_online",                  // every reconnect — too chatty
  "device_screenshot_uploaded",     // routine on-demand artifact
  "device_force_refresh_set",       // admin already knows
  "device_force_refresh_cleared",   // pure plumbing
  "device_command_queued",          // admin already knows
  "device_command_completed",       // routine; only failures matter
]);

/**
 * Sort priority for the Live Screens widget: problems first so an
 * operator's eyes land on them before scrolling past green rows.
 *   1. offline / disabled — needs attention
 *   2. updating           — in-flight, watch
 *   3. online             — healthy
 */
const DEVICE_STATUS_PRIORITY: Record<string, number> = {
  offline: 0, disabled: 0,
  updating: 1,
  online: 2,
};
const sortDevicesByPriority = (devices: IDashboardDevice[]): IDashboardDevice[] =>
  [...devices].sort((a, b) => {
    const pa = DEVICE_STATUS_PRIORITY[a.status] ?? 3;
    const pb = DEVICE_STATUS_PRIORITY[b.status] ?? 3;
    if (pa !== pb) return pa - pb;
    // Within the same band, freshly-seen first.
    const ta = a.last_seen_at ? new Date(a.last_seen_at).getTime() : 0;
    const tb = b.last_seen_at ? new Date(b.last_seen_at).getTime() : 0;
    return tb - ta;
  });

const formatBytes = (b: number): string => {
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
  return `${(b / 1073741824).toFixed(2)} GB`;
};

const formatRelative = (iso: string | null): string => {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

/* ──────────────────────────────────────────────────────────────────────── */

const MetricCard: React.FC<{
  label: string;
  value: number | string;
  icon: React.ReactElement;
  tint: string;
  bg: string;
  subValue?: string;
  emphasised?: boolean;
}> = ({ label, value, icon, tint, bg, subValue, emphasised }) => (
  <Paper elevation={0} sx={{
    p: 2.5, borderRadius: "16px",
    border: `1px solid ${emphasised ? alpha(GOLD, 0.4) : BORDER}`,
    boxShadow: emphasised
      ? `0 8px 24px ${alpha(GOLD, 0.18)}`
      : "0 2px 6px rgba(11,30,45,0.05)",
    height: "100%",
    bgcolor: "#fff",
    position: "relative",
    overflow: "hidden",
  }}>
    {emphasised && (
      <Box sx={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
      }} />
    )}
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
      <Avatar sx={{ bgcolor: bg, color: tint, width: 40, height: 40 }}>
        {React.cloneElement(icon, { sx: { fontSize: 20 } })}
      </Avatar>
      <Typography sx={{
        fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY,
        textTransform: "uppercase", letterSpacing: "0.05em",
      }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{
      fontSize: 34, fontWeight: 800, color: NAVY, lineHeight: 1.1, letterSpacing: "-0.02em",
    }}>
      {value}
    </Typography>
    {subValue && (
      <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 0.5 }}>
        {subValue}
      </Typography>
    )}
  </Paper>
);

const QuickActionCard: React.FC<{
  label: string;
  description: string;
  icon: React.ReactElement;
  onClick: () => void;
  disabled?: boolean;
}> = ({ label, description, icon, onClick, disabled }) => (
  <Paper
    elevation={0}
    onClick={disabled ? undefined : onClick}
    sx={{
      p: 2.5, borderRadius: "16px",
      border: `1px solid ${BORDER}`,
      bgcolor: "#fff",
      cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.6 : 1,
      transition: "all 0.2s ease",
      "&:hover": disabled ? {} : {
        borderColor: alpha(GOLD, 0.6),
        boxShadow: `0 8px 22px ${alpha(GOLD, 0.18)}`,
        transform: "translateY(-2px)",
      },
      display: "flex", alignItems: "center", gap: 2,
    }}
  >
    <Box sx={{
      width: 44, height: 44, borderRadius: 2.5,
      background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
      color: NAVY_DARK,
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: `0 4px 12px ${alpha(GOLD, 0.4)}`,
    }}>
      {React.cloneElement(icon, { sx: { fontSize: 22 } })}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY }}>
        {description}
      </Typography>
    </Box>
  </Paper>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{
    fontSize: 11, fontWeight: 700, color: NAVY,
    textTransform: "uppercase", letterSpacing: "0.08em", mb: 1.5,
  }}>
    {children}
  </Typography>
);

const NeedsAttentionCard: React.FC<{
  offline: number; disabled: number; warning: number;
}> = ({ offline, disabled, warning }) => {
  // Headline = the actionable set. Disabled is informational only —
  // an operator who intentionally turned a screen off shouldn't see
  // the same number tick up that drives them to investigate.
  const headline = offline + warning;
  const isAlerting = headline > 0;
  return (
    <Paper elevation={0} sx={{
      p: 2.5, borderRadius: "16px",
      border: `1px solid ${isAlerting ? alpha("#ef4444", 0.4) : BORDER}`,
      boxShadow: "0 2px 6px rgba(11,30,45,0.05)",
      height: "100%", bgcolor: "#fff", position: "relative", overflow: "hidden",
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
        <Avatar sx={{
          bgcolor: isAlerting ? "#fee2e2" : "#f1f5f9",
          color:   isAlerting ? "#dc2626" : "#94a3b8",
          width: 40, height: 40,
        }}>
          <WifiOffIcon sx={{ fontSize: 20 }} />
        </Avatar>
        <Typography sx={{
          fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY,
          textTransform: "uppercase", letterSpacing: "0.05em",
        }}>
          Needs Attention
        </Typography>
      </Box>
      <Typography sx={{
        fontSize: 34, fontWeight: 800,
        color: isAlerting ? "#dc2626" : NAVY,
        lineHeight: 1.1, letterSpacing: "-0.02em",
      }}>
        {headline}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.75, mt: 1, flexWrap: "wrap" }}>
        <SubCountChip label="offline"  value={offline}  color="#ef4444" />
        <SubCountChip label="warning"  value={warning}  color="#f59e0b" />
        <SubCountChip label="disabled" value={disabled} color="#94a3b8" />
      </Box>
    </Paper>
  );
};

const SubCountChip: React.FC<{ label: string; value: number; color: string }> = ({
  label, value, color,
}) => (
  <Box sx={{
    display: "inline-flex", alignItems: "center", gap: 0.5,
    px: 1, py: 0.25, borderRadius: 999,
    bgcolor: value > 0 ? alpha(color, 0.10) : "#f8fafc",
    border: `1px solid ${value > 0 ? alpha(color, 0.25) : BORDER}`,
  }}>
    <Box sx={{
      width: 6, height: 6, borderRadius: "50%",
      bgcolor: value > 0 ? color : "#cbd5e1",
    }} />
    <Typography sx={{
      fontSize: 10.5, fontWeight: 800,
      color: value > 0 ? color : TEXT_SECONDARY,
    }}>
      {value}
    </Typography>
    <Typography sx={{
      fontSize: 10, color: TEXT_SECONDARY, fontWeight: 600,
      textTransform: "uppercase", letterSpacing: "0.04em",
    }}>
      {label}
    </Typography>
  </Box>
);

const STATUS_PALETTE: Record<string, { bg: string; fg: string; dot: string; label: string }> = {
  online:   { bg: "#dcfce7", fg: "#166534", dot: "#10b981", label: "ONLINE" },
  offline:  { bg: "#fee2e2", fg: "#991b1b", dot: "#ef4444", label: "OFFLINE" },
  disabled: { bg: "#f1f5f9", fg: "#475569", dot: "#94a3b8", label: "DISABLED" },
  updating: { bg: "#fef3c7", fg: "#92400e", dot: "#f59e0b", label: "UPDATING" },
};

const StatusDot: React.FC<{ status: string }> = ({ status }) => {
  const p = STATUS_PALETTE[status] || STATUS_PALETTE.offline;
  return (
    <Box sx={{
      width: 10, height: 10, borderRadius: "50%",
      bgcolor: p.dot, boxShadow: `0 0 0 3px ${alpha(p.dot, 0.18)}`, flexShrink: 0,
    }} />
  );
};

/**
 * Tiny thumbnail of the device's most recent screenshot. Falls back
 * to a neutral placeholder so the row's visual rhythm stays constant
 * across devices that have / haven't uploaded a shot yet.
 */
const ScreenThumb: React.FC<{ url: string | null; status: string }> = ({ url, status }) => {
  const dim = status === "offline" || status === "disabled" ? 0.4 : 1;
  return (
    <Box sx={{
      width: 56, height: 32, borderRadius: 1,
      border: `1px solid ${BORDER}`,
      bgcolor: "#0b1e2d",
      flexShrink: 0, overflow: "hidden",
      display: "flex", alignItems: "center", justifyContent: "center",
      opacity: dim,
    }}>
      {url ? (
        <Box component="img" src={url} alt=""
          sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <TvIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.35)" }} />
      )}
    </Box>
  );
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const p = STATUS_PALETTE[status] || STATUS_PALETTE.offline;
  return (
    <Chip size="small" label={p.label}
      sx={{ height: 18, fontSize: 9.5, fontWeight: 800, letterSpacing: "0.05em",
        bgcolor: p.bg, color: p.fg }} />
  );
};

const SUB_STATUS_PALETTE: Record<string, { bg: string; fg: string }> = {
  trial:     { bg: "#dbeafe", fg: "#1e40af" },
  active:    { bg: "#dcfce7", fg: "#166534" },
  past_due:  { bg: "#fef3c7", fg: "#92400e" },
  suspended: { bg: "#fee2e2", fg: "#991b1b" },
  cancelled: { bg: "#f1f5f9", fg: "#475569" },
};

const SubStatusChip: React.FC<{ status: string }> = ({ status }) => {
  const p = SUB_STATUS_PALETTE[status] || SUB_STATUS_PALETTE.cancelled;
  return (
    <Chip size="small" label={status.toUpperCase()}
      sx={{ height: 16, fontSize: 9, fontWeight: 800, letterSpacing: "0.05em",
        bgcolor: p.bg, color: p.fg }} />
  );
};

const TrialChip: React.FC<{ days: number }> = ({ days }) => {
  // < 0 → already past expiry; ≤ 3 → about to expire; otherwise quiet.
  const expired = days < 0;
  const ending = days >= 0 && days <= 3;
  if (!expired && !ending && days > 14) {
    // > 2 weeks — too far out to worry about; hide.
    return null;
  }
  const palette = expired
    ? { bg: "#fee2e2", fg: "#991b1b" }
    : ending
      ? { bg: "#fef3c7", fg: "#92400e" }
      : { bg: "#dbeafe", fg: "#1e40af" };
  return (
    <Chip size="small"
      label={expired ? "TRIAL EXPIRED" : `${days}d LEFT`}
      sx={{ height: 16, fontSize: 9, fontWeight: 800, letterSpacing: "0.05em",
        bgcolor: palette.bg, color: palette.fg }} />
  );
};

const MiniStat: React.FC<{
  icon: React.ReactElement; tint: string; label: string; value: React.ReactNode;
}> = ({ icon, tint, label, value }) => (
  <Box sx={{
    p: 1.5, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: "#f8fafc",
    display: "flex", alignItems: "center", gap: 1.5,
  }}>
    <Avatar sx={{ bgcolor: alpha(tint, 0.12), color: tint, width: 28, height: 28 }}>
      {React.cloneElement(icon, { sx: { fontSize: 15 } })}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: TEXT_SECONDARY,
        textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 800, color: NAVY }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const HEALTH_PILL: Record<string, { bg: string; fg: string }> = {
  ok:       { bg: "#dcfce7", fg: "#166534" },
  true:     { bg: "#dcfce7", fg: "#166534" },
  warning:  { bg: "#fef3c7", fg: "#92400e" },
  unknown:  { bg: "#f1f5f9", fg: "#475569" },
  disabled: { bg: "#f1f5f9", fg: "#475569" },
  down:     { bg: "#fee2e2", fg: "#991b1b" },
  false:    { bg: "#fee2e2", fg: "#991b1b" },
};

const HealthRow: React.FC<{ label: string; value: any }> = ({ label, value }) => {
  const key = String(value).toLowerCase();
  const p = HEALTH_PILL[key] || HEALTH_PILL.unknown;
  const display = value === true ? "OK" : value === false ? "FAIL" : String(value).toUpperCase();
  return (
    <Box sx={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      py: 0.85, borderBottom: `1px solid ${BORDER}`, "&:last-child": { borderBottom: "none" },
    }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: TEXT_PRIMARY }}>
        {label}
      </Typography>
      <Chip size="small" label={display}
        sx={{ height: 18, fontSize: 10, fontWeight: 800, bgcolor: p.bg, color: p.fg }} />
    </Box>
  );
};

const EmptyState: React.FC<{ icon: React.ReactElement; title: string; message: string }> = ({
  icon, title, message,
}) => (
  <Box sx={{ p: 5, textAlign: "center", color: TEXT_SECONDARY }}>
    {React.cloneElement(icon, { sx: { fontSize: 36, opacity: 0.5, mb: 1.5 } })}
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: 11.5, mt: 0.5 }}>
      {message}
    </Typography>
  </Box>
);

/* ──────────────────────────────────────────────────────────────────────── */

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const isSuper   = useAppSelector(selectIsSuperAdmin);
  const isContent = useAppSelector(selectIsContentManager);

  const { data, isLoading, refetch } = useGetDashboardSummaryQuery(undefined, {
    pollingInterval: 30_000,
    refetchOnFocus: true,
  });
  const { data: healthData } = useGetSystemHealthQuery(undefined, {
    skip: !isSuper, pollingInterval: 60_000,
  });
  const [createPlaylist, { isLoading: isCreatingPlaylist }] = useCreatePlaylistMutation();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { enqueue, hasActive } = useUploads();
  const [playlistDialog, setPlaylistDialog] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState("");

  if (isLoading) return <PageLoader height="20vh" />;
  const summary = data?.data as unknown as DashboardData | undefined;
  const health  = (healthData as any) || null;

  const handleUploadClick = () => fileInputRef.current?.click();
  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const results = await Promise.allSettled(
      Array.from(files).map(f => enqueue(f))
    );
    const ok = results.filter(r => r.status === "fulfilled").length;
    const failed = results.filter(r => r.status === "rejected");
    if (ok > 0) {
      showMessage(`Uploaded ${ok} file${ok === 1 ? "" : "s"}`);
      refetch();
    }
    failed.forEach((r: any) => {
      const msg = r.reason?.message || "Upload failed";
      if (msg && !msg.toLowerCase().includes("cancel")) showErrorMessage(msg);
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCreatePlaylist = async () => {
    if (!newPlaylistName.trim()) { showErrorMessage("Name is required"); return; }
    try {
      const res = await createPlaylist({
        name: newPlaylistName.trim(), is_active: true,
      }).unwrap();
      if (res.status) {
        const created = (res as any).data;
        showMessage("Playlist created");
        setPlaylistDialog(false); setNewPlaylistName("");
        if (created?.id) navigate(`/playlist/edit/${created.id}`);
      }
    } catch {
      showErrorMessage("Failed to create playlist");
    }
  };

  // Filter noisy events, then take 5. Surfacing only signal lets the
  // dashboard be a glance-and-go status board.
  const activity = (summary?.recent_activity ?? [])
    .filter((a) => !NOISY_ACTIVITY_TYPES.has(a.type))
    .slice(0, 5);
  const devices = summary?.devices;
  const media   = summary?.media;
  const recent  = sortDevicesByPriority(summary?.recent_devices ?? []);
  const companies = summary?.companies ?? [];
  const saas    = summary?.saas_revenue ?? null;

  return (
    <Box sx={{ bgcolor: PAGE_BG, minHeight: "calc(100vh - 100px)", mx: -3, mt: -3 }}>
      {/* ── Welcome Header ── */}
      <Box sx={{ bgcolor: "#fff", borderBottom: `1px solid ${BORDER}`, px: 3, py: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 3,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 100%)`,
            boxShadow: `0 6px 18px ${alpha(NAVY, 0.35)}`,
            border: `1px solid ${alpha(GOLD, 0.35)}`,
          }}>
            <TrendingUpIcon sx={{ fontSize: 22, color: GOLD }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography fontSize={22} fontWeight={800} color={NAVY} letterSpacing="-0.02em">
              Dashboard
            </Typography>
            <Typography fontSize={12} color={TEXT_SECONDARY}>
              {isSuper ? "Fleet-wide overview" :
                isContent ? "Content overview" : "Your company overview"}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Content ── */}
      <Box sx={{ p: 3 }}>

        {/* ── Top row: Screen operations (always visible) ── */}
        <SectionLabel>Screens</SectionLabel>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              label="Total Screens"
              value={devices?.total ?? 0}
              icon={<TvIcon />}
              tint="#3b82f6" bg="#dbeafe"
              subValue={`${devices?.total ?? 0} registered · ${devices?.active_billable ?? 0} billable`}
              emphasised
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              label="Online Screens"
              value={devices?.online ?? 0}
              icon={<FiberManualRecordIcon />}
              tint="#10b981" bg="#d1fae5"
              subValue="Heartbeat within 5 min"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {/* "Needs Attention" replaces the single Offline tile.
                Headline number is offline + warning (the actionable set);
                disabled count is shown as a chip but does NOT inflate
                the headline because an intentionally-disabled screen
                isn't a problem to fix. */}
            <NeedsAttentionCard
              offline={devices?.offline ?? 0}
              disabled={devices?.disabled ?? 0}
              warning={devices?.warning ?? 0}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              label="Critical Alerts"
              value={devices?.alerts_24h ?? 0}
              icon={<NotificationsActiveIcon />}
              tint={(devices?.alerts_24h ?? 0) > 0 ? "#dc2626" : "#10b981"}
              bg={(devices?.alerts_24h ?? 0) > 0 ? "#fee2e2" : "#d1fae5"}
              subValue="Offline events, last 24h"
            />
          </Grid>
        </Grid>

        {/* ── Quick Actions (gated by role) ── */}
        {!isContent && (
          <>
            <SectionLabel>Quick Actions</SectionLabel>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <QuickActionCard
                  label="Add Device"
                  description="Provision a new screen"
                  icon={<AddIcon />}
                  onClick={() => navigate("/device/create")}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <QuickActionCard
                  label={hasActive ? "Uploading…" : "Upload Media"}
                  description="Images / MP4 videos"
                  icon={<CloudUploadIcon />}
                  onClick={handleUploadClick}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <QuickActionCard
                  label="Create Playlist"
                  description="Build a media sequence"
                  icon={<PlaylistAddIcon />}
                  onClick={() => setPlaylistDialog(true)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <QuickActionCard
                  label="Invite User"
                  description="Add a teammate"
                  icon={<PersonAddIcon />}
                  onClick={() => navigate("/signage-users")}
                />
              </Grid>
            </Grid>
          </>
        )}

        {/* Content-manager-focused quick actions — shorter set */}
        {isContent && (
          <>
            <SectionLabel>Quick Actions</SectionLabel>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <QuickActionCard
                  label={hasActive ? "Uploading…" : "Upload Media"}
                  description="Images / MP4 videos"
                  icon={<CloudUploadIcon />}
                  onClick={handleUploadClick}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <QuickActionCard
                  label="Create Playlist"
                  description="Build a media sequence"
                  icon={<PlaylistAddIcon />}
                  onClick={() => setPlaylistDialog(true)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <QuickActionCard
                  label="Manage Playlists"
                  description="Reorder + assign content"
                  icon={<PlaylistPlayIcon />}
                  onClick={() => navigate("/playlist/list")}
                />
              </Grid>
            </Grid>
          </>
        )}

        {/* ── Two-column body ── */}
        <Grid container spacing={3}>
          {/* LEFT — Live Screens (always) + Recent Activity */}
          <Grid item xs={12} lg={8}>
            {/* Live Screens widget */}
            <Paper elevation={0} sx={{
              mb: 3, borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff", overflow: "hidden",
            }}>
              <Box sx={{ px: 2.5, py: 2,
                display: "flex", alignItems: "center", gap: 1.5,
                borderBottom: `1px solid ${BORDER}` }}>
                <TvIcon sx={{ color: "#3b82f6", fontSize: 20 }} />
                <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 800, color: NAVY }}>
                  Live Screens
                </Typography>
                <Button size="small" endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                  onClick={() => navigate("/monitoring/devices")}
                  sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, color: NAVY }}>
                  View All
                </Button>
              </Box>
              {recent.length === 0 ? (
                <EmptyState icon={<TvIcon />} title="No screens yet"
                  message="Add a device to start tracking screen status here." />
              ) : recent.map((d, i) => (
                <Box key={d.id}
                  onClick={() => navigate(`/devices/${d.id}/monitoring`)}
                  sx={{
                    display: "flex", alignItems: "center", gap: 2,
                    px: 2.5, py: 1.5,
                    borderTop: i === 0 ? "none" : `1px solid ${BORDER}`,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "#f8fafc" },
                  }}>
                  <StatusDot status={d.status} />
                  {/* Thumbnail — last known screenshot or a placeholder so
                      every row has the same visual rhythm. */}
                  <ScreenThumb url={d.screenshot_url} status={d.status} />
                  <Box sx={{ minWidth: 130 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY,
                      fontFamily: "monospace" }}>
                      {d.device_code}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 130 }}>
                      {d.device_name}
                    </Typography>
                  </Box>
                  {isSuper && (
                    <Typography sx={{ fontSize: 12, color: TEXT_PRIMARY,
                      minWidth: 120, whiteSpace: "nowrap",
                      overflow: "hidden", textOverflow: "ellipsis" }}>
                      {d.company_name || "—"}
                    </Typography>
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY,
                      textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>
                      Now Playing
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: TEXT_PRIMARY,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {d.current_media || "—"}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right", minWidth: 110 }}>
                    <StatusPill status={d.status} />
                    <Typography sx={{ fontSize: 10.5, color: TEXT_SECONDARY, mt: 0.5 }}>
                      {formatRelative(d.last_seen_at) || "never"}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Paper>

            {/* Recent Activity (reduced to 5) */}
            <Paper elevation={0} sx={{
              borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff", overflow: "hidden",
            }}>
              <Box sx={{ px: 2.5, py: 2,
                display: "flex", alignItems: "center", gap: 1.5,
                borderBottom: `1px solid ${BORDER}` }}>
                <HistoryIcon sx={{ color: "#64748b", fontSize: 20 }} />
                <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 800, color: NAVY }}>
                  Recent Activity
                </Typography>
                <Button size="small" endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                  onClick={() => navigate("/signage-activity")}
                  sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, color: NAVY }}>
                  View All Activity
                </Button>
              </Box>
              {activity.length === 0 ? (
                <EmptyState icon={<HistoryIcon />} title="No activity yet"
                  message="Register a device, upload media, or build a playlist to see events here." />
              ) : (
                activity.map((a, idx) => {
                  const style = ACTIVITY_STYLE[a.type] ?? {
                    tint: "#64748b", bg: "#f1f5f9", icon: <HistoryIcon />,
                  };
                  return (
                    <Box key={a.id} sx={{
                      display: "flex", alignItems: "center", gap: 2,
                      px: 2.5, py: 1.5,
                      borderTop: idx === 0 ? "none" : `1px solid ${BORDER}`,
                      "&:hover": { bgcolor: "#f8fafc" },
                    }}>
                      <Avatar sx={{ bgcolor: style.bg, color: style.tint, width: 32, height: 32 }}>
                        {React.cloneElement(style.icon, { sx: { fontSize: 16 } })}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
                          {a.label}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: TEXT_SECONDARY,
                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {a.description}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, fontWeight: 500 }}>
                        {formatRelative(a.created_at)}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </Paper>
          </Grid>

          {/* RIGHT — Media, System Health, Client Overview */}
          <Grid item xs={12} lg={4}>
            {/* Media stats */}
            <Paper elevation={0} sx={{
              p: 2.5, mb: 3, borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <PermMediaIcon sx={{ color: "#00838f", fontSize: 20 }} />
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: NAVY }}>
                  Media Library
                </Typography>
              </Box>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <MiniStat icon={<PermMediaIcon />} tint="#00838f"
                    label="Total" value={media?.total ?? 0} />
                </Grid>
                <Grid item xs={6}>
                  <MiniStat icon={<StorageIcon />} tint="#64748b"
                    label="Storage" value={formatBytes(media?.storage_bytes ?? 0)} />
                </Grid>
                <Grid item xs={6}>
                  <MiniStat icon={<ImageIcon />} tint="#3b82f6"
                    label="Images" value={media?.images ?? 0} />
                </Grid>
                <Grid item xs={6}>
                  <MiniStat icon={<OndemandVideoIcon />} tint="#7c3aed"
                    label="Videos" value={media?.videos ?? 0} />
                </Grid>
              </Grid>
            </Paper>

            {/* SaaS Revenue — super admin only. Tenant users never get
                this block; revenue fields are also null in payload for
                them, so there's nothing to leak. */}
            {isSuper && saas && (
              <Paper elevation={0} sx={{
                p: 2.5, mb: 3, borderRadius: 3, border: `1px solid ${alpha(GOLD, 0.35)}`,
                background: `linear-gradient(135deg, ${alpha(GOLD, 0.04)} 0%, ${alpha(GOLD, 0.01)} 100%)`,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                  <Box sx={{
                    width: 28, height: 28, borderRadius: 1.5,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
                    color: "#0b1e2d",
                  }}>
                    <TrendingUpIcon sx={{ fontSize: 16 }} />
                  </Box>
                  <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 800, color: NAVY }}>
                    Subscription Revenue
                  </Typography>
                  <Button size="small" onClick={() => navigate("/billing/devices")}
                    sx={{ textTransform: "none", fontSize: 11, color: TEXT_SECONDARY }}>
                    Detail →
                  </Button>
                </Box>
                <Typography sx={{
                  fontSize: 10.5, fontWeight: 700, color: TEXT_SECONDARY,
                  textTransform: "uppercase", letterSpacing: "0.06em",
                }}>
                  Monthly Recurring
                </Typography>
                <Typography sx={{
                  fontSize: 28, fontWeight: 800, color: GOLD_SOFT,
                  fontFamily: "monospace", lineHeight: 1.1, mt: 0.25,
                }}>
                  ${saas.mrr.toFixed(2)}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
                  <Box sx={{
                    flex: 1, p: 1, borderRadius: 1.5,
                    bgcolor: "#f8fafc", border: `1px solid ${BORDER}`,
                  }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: TEXT_SECONDARY,
                      textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      Billable
                    </Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: NAVY }}>
                      {saas.billable_devices}
                    </Typography>
                  </Box>
                  <Box sx={{
                    flex: 1, p: 1, borderRadius: 1.5,
                    bgcolor: "#f8fafc", border: `1px solid ${BORDER}`,
                  }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: TEXT_SECONDARY,
                      textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      Avg / device
                    </Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: NAVY,
                      fontFamily: "monospace" }}>
                      ${saas.avg_per_device.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            )}

            {/* System Health — super admin only */}
            {isSuper && health && (
              <Paper elevation={0} sx={{
                p: 2.5, mb: 3, borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff",
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                  <MonitorHeartIcon sx={{ color: "#10b981", fontSize: 20 }} />
                  <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 800, color: NAVY }}>
                    System Health
                  </Typography>
                  <Button size="small" onClick={() => navigate("/system/health")}
                    sx={{ textTransform: "none", fontSize: 11, color: TEXT_SECONDARY }}>
                    Details →
                  </Button>
                </Box>
                {/* Active Devices ratio — replaces the old Fleet Uptime %
                    that flashed red whenever a single device dropped offline.
                    Neutral navy ratio reads as a status fact, not a failure. */}
                <Box sx={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  py: 0.85, mb: 0.5, borderBottom: `1px solid ${BORDER}`,
                }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: TEXT_PRIMARY }}>
                    Active Devices
                  </Typography>
                  <Typography sx={{
                    fontSize: 14, fontWeight: 800, color: NAVY, fontFamily: "monospace",
                  }}>
                    {devices?.online ?? 0}/{devices?.total ?? 0}
                  </Typography>
                </Box>
                <HealthRow label="API"        value={health.api} />
                <HealthRow label="Database"   value={health.database} />
                <HealthRow label="Storage"    value={health.storage_link} />
                <HealthRow label="Queue"      value={health.queue} />
                <HealthRow label="Scheduler"  value={health.scheduler} />
              </Paper>
            )}

            {/* Client Overview — super admin only */}
            {isSuper && companies && companies.length > 0 && (
              <Paper elevation={0} sx={{
                borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff", overflow: "hidden",
              }}>
                <Box sx={{ px: 2.5, py: 2,
                  display: "flex", alignItems: "center", gap: 1.5,
                  borderBottom: `1px solid ${BORDER}` }}>
                  <BusinessIcon sx={{ color: "#7c3aed", fontSize: 20 }} />
                  <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 800, color: NAVY }}>
                    Client Overview
                  </Typography>
                  <Button size="small" onClick={() => navigate("/billing/devices")}
                    sx={{ textTransform: "none", fontSize: 11, color: TEXT_SECONDARY }}>
                    Billing →
                  </Button>
                </Box>
                {companies.slice(0, 8).map((c, i) => {
                  // Trial countdown — null when company isn't on trial.
                  const trialExpired = c.trial_days_remaining !== null
                    && c.trial_days_remaining < 0;
                  const trialEnding = c.trial_days_remaining !== null
                    && c.trial_days_remaining >= 0
                    && c.trial_days_remaining <= 3;
                  // Device usage progress when a cap is set.
                  const cap = c.max_devices ?? null;
                  const usagePercent = cap && cap > 0
                    ? Math.min(100, (c.device_count / cap) * 100) : 0;
                  const nearLimit = cap !== null && cap > 0
                    && c.device_count / cap >= 0.85;
                  const atLimit = cap !== null && c.device_count >= cap;
                  return (
                    <Box key={c.id}
                      onClick={() => navigate(`/company/${c.id}`)}
                      sx={{
                        display: "flex", flexDirection: "column", gap: 0.75,
                        px: 2.5, py: 1.5,
                        borderTop: i === 0 ? "none" : `1px solid ${BORDER}`,
                        cursor: "pointer",
                        bgcolor: trialExpired || atLimit ? alpha("#fef2f2", 0.6) : undefined,
                        "&:hover": { bgcolor: "#f8fafc" },
                      }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY,
                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {c.name}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25,
                            flexWrap: "wrap" }}>
                            <SubStatusChip status={c.subscription_status} />
                            {c.trial_days_remaining !== null && (
                              <TrialChip days={c.trial_days_remaining} />
                            )}
                          </Box>
                        </Box>
                        {/* Revenue: super admin only — always true in this branch. */}
                        <Box sx={{ textAlign: "right" }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 800, color: GOLD_SOFT,
                            fontFamily: "monospace" }}>
                            ${c.monthly_revenue.toFixed(0)}
                          </Typography>
                          <Typography sx={{ fontSize: 10, color: TEXT_SECONDARY }}>
                            / month
                          </Typography>
                        </Box>
                      </Box>
                      {/* Device usage progress bar (only shown when a cap exists) */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography sx={{
                          fontSize: 11, fontWeight: 700,
                          color: atLimit ? "#b91c1c"
                            : nearLimit ? "#92400e" : TEXT_SECONDARY,
                          minWidth: 64,
                        }}>
                          {cap !== null
                            ? `${c.device_count}/${cap} devices`
                            : `${c.device_count} devices`}
                        </Typography>
                        {cap !== null && cap > 0 && (
                          <Box sx={{
                            flex: 1, height: 4, borderRadius: 2,
                            bgcolor: "#f1f5f9", overflow: "hidden",
                          }}>
                            <Box sx={{
                              width: `${usagePercent}%`, height: "100%",
                              bgcolor: atLimit ? "#dc2626"
                                : nearLimit ? "#f59e0b" : "#10b981",
                              transition: "width 0.3s ease",
                            }} />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Paper>
            )}
          </Grid>
        </Grid>
      </Box>

      <input
        ref={fileInputRef} type="file"
        accept=".jpg,.jpeg,.png,.webp,.mp4"
        multiple hidden
        onChange={(e) => handleFiles(e.target.files)}
      />

      <Dialog
        open={playlistDialog} onClose={() => setPlaylistDialog(false)}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: NAVY, pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: 2,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: `linear-gradient(135deg, ${alpha("#7c3aed", 0.12)} 0%, ${alpha("#7c3aed", 0.06)} 100%)`,
            }}>
              <PlaylistAddIcon sx={{ fontSize: 16, color: "#7c3aed" }} />
            </Box>
            Create Playlist
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{
            fontSize: 12, fontWeight: 600, color: "#475569",
            mb: 0.75, textTransform: "uppercase", letterSpacing: "0.02em",
          }}>
            Playlist Name *
          </Typography>
          <TextField
            autoFocus fullWidth size="small"
            placeholder="e.g. Morning Loop"
            value={newPlaylistName}
            onChange={(e) => setNewPlaylistName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleCreatePlaylist(); }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2.5, fontSize: 13, bgcolor: "#fff",
                "& fieldset": { borderColor: "#e2e8f0" },
                "&.Mui-focused fieldset": { borderColor: "#7c3aed", borderWidth: 2 },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setPlaylistDialog(false)}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, color: "#64748b" }}>
            Cancel
          </Button>
          <Button variant="contained" disableElevation
            disabled={isCreatingPlaylist} onClick={handleCreatePlaylist}
            sx={{
              borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 700,
              background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
              "&:hover": { background: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)" },
            }}>
            {isCreatingPlaylist ? "Creating…" : "Create & Edit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
